<ng-template #modal let-modal>
  <div class="modal-header">
    <h4 class="modal-title">License Change Analysis</h4>
    <button type="button" class="close" aria-label="Close" (click)="modal.dismiss('Cross click')">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body license-analysis-modal-body">
    <!-- Date Selection -->
    <div class="row mb-4">
      <div class="col-md-6">
        <label for="startDate">Start Date</label>
        <app-datepicker
          id="startDate"
          [(model)]="startDate"
          (modelChange)="onDateChange()"
          placeholder="Select start date">
        </app-datepicker>
      </div>
      <div class="col-md-6">
        <label for="endDate">End Date</label>
        <app-datepicker
          id="endDate"
          [(model)]="endDate"
          (modelChange)="onDateChange()"
          placeholder="Select end date">
        </app-datepicker>
      </div>
    </div>
    


    <!-- Loading Spinner -->
    <div *ngIf="loading" class="text-center">
      <app-loading-spinner [text]="'Loading License Data'"></app-loading-spinner>
    </div>

    <!-- Analysis Results -->
    <div *ngIf="!loading" class="analysis-results">
      <div class="row">
        <!-- Section 1: Start Date Summary -->
        <div class="col-md-3">
          <div class="analysis-section">
            <h5 class="section-title">
              <i class="fas fa-calendar-day text-primary mr-2"></i>
              Start Date<br>
              <small class="text-muted">{{ startDate | date:'mediumDate' }}</small>
            </h5>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="summary-label">Purchases</div>
                <div class="summary-value">{{ formatNumber(startDateSummary.purchaseCount) }}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Total Licenses</div>
                <div class="summary-value">{{ formatNumber(startDateSummary.licenseCount) }}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Total Dollar Amount</div>
                <div class="summary-value">{{ formatCurrency(startDateSummary.totalDollarAmount) }}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">ARR Dollar Amount</div>
                <div class="summary-value">{{ formatCurrency(startDateSummary.arrDollarAmount) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Section 2: Expired Licenses -->
        <div class="col-md-3">
          <div class="analysis-section">
            <h5 class="section-title">
              <i class="fas fa-calendar-times text-danger mr-2"></i>
              Expired Since<br>
              <small class="text-muted">{{ startDate | date:'mediumDate' }}</small>
            </h5>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="summary-label">Purchases</div>
                <div class="summary-value">({{ formatNumber(expiredSummary.purchaseCount) }})</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Total Licenses</div>
                <div class="summary-value">({{ formatNumber(expiredSummary.licenseCount) }})</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Total Dollar Amount</div>
                <div class="summary-value">({{ formatCurrency(expiredSummary.totalDollarAmount) }})</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">ARR Dollar Amount</div>
                <div class="summary-value">({{ formatCurrency(expiredSummary.arrDollarAmount) }})</div>
              </div>
            </div>
          </div>
          <!-- Expired Organizations List -->
          <div class="organizations-list mt-2">
            <div *ngIf="expiredLicenses && expiredLicenses.length > 0; else noExpiredOrgs">
              <div class="org-item" *ngFor="let license of expiredLicenses">
                {{ license.PurchasingOrganizationDescription || 'Unknown Organization' }}
              </div>
            </div>
            <ng-template #noExpiredOrgs>
              <div class="org-item text-muted">No expired licenses</div>
            </ng-template>
          </div>
        </div>

        <!-- Section 3: New Licenses -->
        <div class="col-md-3">
          <div class="analysis-section">
            <h5 class="section-title">
              <i class="fas fa-calendar-plus text-success mr-2"></i>
              New Since<br>
              <small class="text-muted">{{ startDate | date:'mediumDate' }}</small>
            </h5>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="summary-label">Purchases</div>
                <div class="summary-value">{{ formatNumber(newSummary.purchaseCount) }}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Total Licenses</div>
                <div class="summary-value">{{ formatNumber(newSummary.licenseCount) }}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Total Dollar Amount</div>
                <div class="summary-value">{{ formatCurrency(newSummary.totalDollarAmount) }}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">ARR Dollar Amount</div>
                <div class="summary-value">{{ formatCurrency(newSummary.arrDollarAmount) }}</div>
              </div>
            </div>
          </div>
          <!-- New Organizations List -->
          <div class="organizations-list mt-2">
            <div *ngIf="newLicenses && newLicenses.length > 0; else noNewOrgs">
              <div class="org-item" *ngFor="let license of newLicenses">
                {{ license.PurchasingOrganizationDescription || 'Unknown Organization' }}
              </div>
            </div>
            <ng-template #noNewOrgs>
              <div class="org-item text-muted">No new licenses</div>
            </ng-template>
          </div>
        </div>

        <!-- Section 4: End Date Summary -->
        <div class="col-md-3">
          <div class="analysis-section">
            <h5 class="section-title">
              <i class="fas fa-calendar-check text-info mr-2"></i>
              End Date<br>
              <small class="text-muted">{{ endDate | date:'mediumDate' }}</small>
            </h5>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="summary-label">Purchases</div>
                <div class="summary-value">{{ formatNumber(endDateSummary.purchaseCount) }}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Total Licenses</div>
                <div class="summary-value">{{ formatNumber(endDateSummary.licenseCount) }}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Total Dollar Amount</div>
                <div class="summary-value">{{ formatCurrency(endDateSummary.totalDollarAmount) }}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">ARR Dollar Amount</div>
                <div class="summary-value">{{ formatCurrency(endDateSummary.arrDollarAmount) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="btn btn-secondary" (click)="cancel()">Close</button>
  </div>
</ng-template>
